// ============================================================================
// OFFICE SERVICE
// ============================================================================
// Business logic and HTTP calls for the Office (Location) master.
//
// Backend wraps every response in ApiResponse<T> with this shape:
//   { success, message, data, errors, correlationId, timestamp }
//
// List endpoints additionally wrap data in PagedResult<T>:
//   data: { items, page, pageSize, totalCount, totalPages, hasPrevious, hasNext }
//
// The frontend grids (SyutGrid) consume PaginatedResponse<T> with shape:
//   { success, message, data: T[], pagination: { currentPage, pageSize, totalPages, totalItems, hasNextPage, hasPreviousPage } }
//
// `getAll()` MUST do this transformation — see CLAUDE.md "Service layer transformation".

import { get, post, put, del } from '../client/axiosClient';
import { officeEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  Office,
  CreateOfficeRequest,
  UpdateOfficeRequest,
  OfficeSearchRequest,
} from '../types';

class OfficeService {
  /**
   * Get paginated list with optional filters.
   * Transforms backend PagedResult<T> → frontend PaginatedResponse<T>.
   */
  async getAll(
    params?: OfficeSearchRequest
  ): Promise<PaginatedResponse<Office>> {
    const endpoint = params
      ? buildEndpointWithQuery(officeEndpoints.basePath(), params as Record<string, any>)
      : officeEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Office>> {
    const response = await get<ApiResponse<Office>>(
      officeEndpoints.getById(id)
    );
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Office>> {
    const response = await get<ApiResponse<Office>>(
      officeEndpoints.getByCode(code)
    );
    return response.data;
  }

  async create(
    data: CreateOfficeRequest
  ): Promise<ApiResponse<Office>> {
    const response = await post<ApiResponse<Office>>(
      officeEndpoints.create(),
      data
    );
    return response.data;
  }

  async update(
    id: ID,
    data: UpdateOfficeRequest
  ): Promise<ApiResponse<Office>> {
    const response = await put<ApiResponse<Office>>(
      officeEndpoints.update(id),
      data
    );
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(officeEndpoints.delete(id));
    return { success: true };
  }
}

export const officeService = new OfficeService();
export default officeService;
